"use client";

import { useState, type ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { MenuActions, type ActionMenu } from "./MenuActions";

const euros = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

/** L'en-tête de page : titre, sous-titre, action principale à droite. */
export function EnTeteDeListe({ titre, sousTitre, action }: { titre: string; sousTitre: string; action: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{titre}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{sousTitre}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

/** La synthèse compacte : quatre nombres sur une ligne, aucune carte. */
export function SyntheseCompacte({ lignes }: {
  lignes: { libelle: string; montant: number | null; couleur?: string; titre?: string; sous?: ReactNode }[];
}) {
  return (
    <dl className={cn(
      "grid grid-cols-2 gap-x-6 gap-y-3 glass-card rounded-xl px-4 py-3 sm:px-5",
      lignes.length >= 5 ? "md:grid-cols-3 xl:grid-cols-5" : "lg:grid-cols-4"
    )}>
      {lignes.map((l) => (
        <div key={l.libelle} className="min-w-0" title={l.titre}>
          <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground leading-tight">{l.libelle}</dt>
          <dd className={cn("mt-0.5 text-base sm:text-lg font-semibold tabular-nums tracking-tight whitespace-nowrap", l.couleur ?? "text-foreground")}>{l.montant === null ? "—" : euros(l.montant)}</dd>
          {l.sous && <dd className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{l.sous}</dd>}
        </div>
      ))}
    </dl>
  );
}

/** Un mois : en-tête repliable, puis son contenu. */
export function GroupeMensuel({ libelle, resume, ouvertParDefaut = true, children }: {
  libelle: string; resume: string; ouvertParDefaut?: boolean; children: ReactNode;
}) {
  const [ouvert, setOuvert] = useState(ouvertParDefaut);
  return (
    <section>
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
        className="flex w-full items-center justify-between gap-3 rounded-lg px-1 py-2 text-left transition-colors hover:bg-black/5 dark:hover:bg-white/5"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{libelle}</span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="tabular-nums">{resume}</span>
          <ChevronDown className={cn("h-4 w-4 transition-transform", !ouvert && "-rotate-90")} />
        </span>
      </button>
      {ouvert && <div className="mt-1">{children}</div>}
    </section>
  );
}

/** Une case à cocher discrète. */
export function Case({ coche, onChange, libelle }: { coche: boolean; onChange: (v: boolean) => void; libelle: string }) {
  return (
    <input
      type="checkbox"
      checked={coche}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      aria-label={libelle}
      className="h-4 w-4 cursor-pointer rounded border-border/70 accent-primary"
    />
  );
}

/** La barre qui n'apparaît qu'avec une sélection. */
export function BarreDeSelection({ nombre, nom, onAnnuler, boutons, actions }: {
  nombre: number; nom: (n: number) => string; onAnnuler: () => void;
  boutons: { libelle: string; icone?: ReactNode; onClick: () => void }[];
  actions?: ActionMenu[];
}) {
  if (nombre === 0) return null;
  return (
    <div className="glass-dropdown fixed bottom-6 left-1/2 z-50 -translate-x-1/2 flex items-center gap-2 rounded-2xl border border-border/60 px-3 py-2 shadow-2xl animate-in slide-in-from-bottom-4 duration-200">
      <span className="px-2 text-sm font-medium text-foreground whitespace-nowrap">{nom(nombre)}</span>
      <div className="h-5 w-px bg-border/60" />
      {boutons.map((b) => (
        <button
          key={b.libelle}
          type="button"
          onClick={b.onClick}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-foreground transition-colors hover:bg-black/5 dark:hover:bg-white/10"
        >
          {b.icone && <span className="[&>svg]:h-4 [&>svg]:w-4 text-muted-foreground">{b.icone}</span>}
          {b.libelle}
        </button>
      ))}
      {actions && actions.length > 0 && <MenuActions actions={actions} titre="Autres actions" />}
      <button type="button" onClick={onAnnuler} title="Annuler la sélection" className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Les pastilles de statut : douces, lisibles, partagées par les deux pages. */
export function couleurDeStatut(libelle: string): string {
  switch (libelle) {
    case "Payée": case "Accepté": case "Facturé": case "Converti":
      return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
    case "Envoyée": case "Envoyé": case "Téléchargée": case "Téléchargé":
      return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
    case "Retard": case "Refusé":
      return "bg-red-500/10 text-red-700 dark:text-red-300";
    case "Partiellement avoirée": case "Annulée par avoir":
      return "bg-amber-500/10 text-amber-700 dark:text-amber-300";
    case "Annulée": case "Archivée": case "Archivé":
      return "bg-slate-500/10 text-slate-600 dark:text-slate-300";
    case "Brouillon":
    default:
      return "bg-slate-500/10 text-slate-600 dark:text-slate-300";
  }
}

export function Pastille({ libelle, titre, onClick, className }: { libelle: string; titre?: string; onClick?: (e: React.MouseEvent) => void; className?: string }) {
  const contenu = (
    <span title={titre} className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap", couleurDeStatut(libelle), className)}>
      {libelle}
    </span>
  );
  if (!onClick) return contenu;
  return (
    <button type="button" onClick={onClick} className="cursor-pointer transition-opacity hover:opacity-80" title="Changer le statut">
      {contenu}
    </button>
  );
}
